import { DateRange, PickersInputComponentLocaleText } from '@mui/x-date-pickers-pro'
import { deDE, enUS, frFR } from '@mui/x-date-pickers/locales'
import dayjs from 'dayjs'
import 'temporal-polyfill/global'

import {
  substract_offset,
  ITemporalRange,
  zonedDateTimeToISOString,
  getJurisdictionTimezone,
} from 'src/commons/temporalHelpers'

export interface IDayjsRange {
  from: dayjs.Dayjs | null
  to: dayjs.Dayjs | null
}

function normalizeDate(value: Temporal.ZonedDateTime | string, substractOffset: boolean): dayjs.Dayjs {
  let normalized = value
  if (typeof value === 'string') {
    // Retrieved from local storage as a string
    const fromValueInEpochNanoseconds = Temporal.Instant.from(value).epochNanoseconds
    normalized = new Temporal.ZonedDateTime(fromValueInEpochNanoseconds, getJurisdictionTimezone())
  } else {
    normalized = substractOffset ? substract_offset(value) : value
  }
  return dayjs(zonedDateTimeToISOString(normalized)).tz(getJurisdictionTimezone())
}

export function getDayjsValues(values: ITemporalRange | undefined, substractOffset = true): IDayjsRange {
  if (!values) {
    return {
      from: null,
      to: null,
    }
  }

  const result: IDayjsRange = { from: null, to: null }

  if (values.from) {
    result.from = normalizeDate(values.from, substractOffset)
  }
  if (values.to) {
    result.to = normalizeDate(values.to, substractOffset)
  }

  return result
}

export function getTimezoneOffset() {
  // dayjs() uses the local user timezone (regardless of `dayjs.tz.setDefault`), which is equivalent to moment.tz.guess()
  // dayjs.tz() on the other hand take the setting into consideration, which is equivalent to moment.tz(timezone)
  // https://day.js.org/docs/en/plugin/timezone
  return dayjs().utcOffset() - dayjs.tz().utcOffset()
}

export function normalize(dateRange: DateRange<dayjs.Dayjs>): DateRange<dayjs.Dayjs> {
  return [
    dateRange[0]?.set('hour', 0).set('minute', 0).set('second', 0) ?? null,
    dateRange[1]?.set('hour', 23).set('minute', 59).set('second', 59) ?? null,
  ]
}

const textLocales: Record<string, typeof enUS> = {
  en: enUS,
  fr: frFR,
  de: deDE,
}

export const getTextLocale = function (language: string): PickersInputComponentLocaleText<dayjs.Dayjs | null> {
  const locale = textLocales[language] ?? enUS
  return locale.components.MuiLocalizationProvider.defaultProps.localeText
}
